import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { QuotationData } from '../../../../models/quotations/Quotations';
import { formatDate } from '../../../../utils/dateFormatter';
import { QuotationDetailsDataItem } from '../../../quotation-details/QuotationDetailsDataItem';
import { CountryNameDisplay } from '../../../shared/CountryNameDisplay';
import { QuotationSummaryTitle } from '../../shared/QuotationSummaryTitle';

const StyledDataWrapper = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'repeat(4, 1fr)',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
}));

export const QuotationSummaryDetails: React.FC<{ quoteData: QuotationData }> = (
    props
) => {
    const { quoteData } = props;
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.SUMMARY.DETAILS',
    });

    return (
        <>
            <div>
                <QuotationSummaryTitle>{t('TITLE')}</QuotationSummaryTitle>
                <StyledDataWrapper>
                    <QuotationDetailsDataItem
                        title={t('DETAILS.QUOTATION_DATES')}
                    >
                        <Typography>
                            {t('DETAILS.QUOTATION_CREATE', {
                                date: formatDate(quoteData.created_at),
                            })}
                        </Typography>
                        <Typography>
                            {t('DETAILS.QUOTATION_EXPIRED', {
                                date: formatDate(quoteData.valid_until),
                            })}
                        </Typography>
                    </QuotationDetailsDataItem>
                    <QuotationDetailsDataItem
                        title={t('DETAILS.SELLER_DETAILS')}
                    >
                        {quoteData && quoteData.seller && (
                            <>
                                <Typography>
                                    {quoteData.seller.company}
                                </Typography>
                                <Typography>
                                    {quoteData.seller.tax_number}
                                </Typography>
                                <Typography>
                                    {quoteData.seller.phone}
                                </Typography>
                            </>
                        )}
                    </QuotationDetailsDataItem>
                    <QuotationDetailsDataItem
                        title={t('DETAILS.BUYER_DETAILS')}
                    >
                        {quoteData && quoteData.buyer && (
                            <>
                                <Typography>
                                    {quoteData.buyer.company}
                                </Typography>
                                <Typography>
                                    {quoteData.buyer.tax_number}
                                </Typography>
                                <Typography>{quoteData.buyer.name}</Typography>
                                <Typography>{quoteData.buyer.phone}</Typography>
                            </>
                        )}
                    </QuotationDetailsDataItem>
                    <QuotationDetailsDataItem
                        title={t('DETAILS.BUYER_ADDRESS')}
                    >
                        {quoteData && quoteData.buyer && (
                            <>
                                <CountryNameDisplay
                                    countryCode={quoteData.buyer.country}
                                />
                                <Typography>
                                    {quoteData.buyer.postcode}{' '}
                                    {quoteData.buyer.city}
                                </Typography>
                                <Typography>
                                    {quoteData.buyer.address1}
                                </Typography>
                                <Typography>
                                    {quoteData.buyer.address2}
                                </Typography>
                            </>
                        )}
                    </QuotationDetailsDataItem>
                </StyledDataWrapper>

                <QuotationDetailsDataItem title={t('DETAILS.PROJECT_NAME')}>
                    <Typography>{quoteData?.buyer?.project_name}</Typography>
                </QuotationDetailsDataItem>

                <Box>
                    {quoteData && quoteData.comment_1 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '5px',
                                py: '10px',
                                paddingRight: '20px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                {t('DETAILS.COMMENT1') + ':'}
                            </Typography>
                            <Typography>{quoteData.comment_1}</Typography>
                        </Box>
                    )}
                </Box>
            </div>
        </>
    );
};
